import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, Loader2 } from 'lucide-react';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([
    { from: 'bot', text: "Hi! I'm Kevin, your AI assistant. How can I help you learn about AIConneqtors today?" }
  ]);
  const messagesEndRef = useRef(null);

  const quickReplies = [
    "What is AIConneqtors?",
    "How do I create a Voice AI?",
    "What are the pricing plans?",
    "Can I try it for free?"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getAIResponse = (userMessage) => {
    const lowerMsg = userMessage.toLowerCase();
    
    if (lowerMsg.includes('what is') || lowerMsg.includes('about')) {
      return "AIConneqtors is a Voice AI platform that lets you build, test, and deploy conversational AI agents in minutes. No coding required! You can create customer support bots, lead generation agents, appointment schedulers, and more.";
    }
    if (lowerMsg.includes('create') || lowerMsg.includes('build') || lowerMsg.includes('how')) {
      return "Creating a Voice AI is easy!\n\n1. Sign up for free\n2. Describe what you want your AI to do\n3. Test it with our simulator\n4. Deploy it on your phone number or website\n\nWant to get started? Click 'Sign Up' in the top right!";
    }
    if (lowerMsg.includes('price') || lowerMsg.includes('cost') || lowerMsg.includes('plan')) {
      return "We have flexible plans:\n\n• Free Trial: 10 minutes free\n• Starter: $15/mo (143 min)\n• Jump Starter: $30/mo (316 min) - Most Popular!\n• Growth: $200/mo (2857 min)\n\nVisit /pricing for full details!";
    }
    if (lowerMsg.includes('free') || lowerMsg.includes('trial')) {
      return "Yes! We offer a Free Trial with 10 minutes of calls included. No credit card required to start. Sign up and create your first Voice AI agent today!";
    }
    if (lowerMsg.includes('integration') || lowerMsg.includes('connect')) {
      return "AIConneqtors integrates with popular tools like:\n\n• CRMs: Salesforce, HubSpot, Zoho\n• Automation: n8n, Make, Zapier\n• Calendars: Google Calendar, Cal.com\n• AI Models: OpenAI, Anthropic, Google\n\nCheck our /integrations page for the full list!";
    }
    if (lowerMsg.includes('phone') || lowerMsg.includes('number')) {
      return "You can buy US (+1) phone numbers directly in AIConneqtors! Connect each number to a specific Voice AI agent. Manage renewals and expiry from your dashboard.";
    }
    
    return "Thanks for your question! For detailed information, I recommend:\n\n• Documentation at /docs\n• Pricing at /pricing\n• Contact us at /contact-us\n\nIs there something specific you'd like to know?";
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    
    const userMsg = message.trim();
    setMessages(prev => [...prev, { from: 'user', text: userMsg }]);
    setMessage('');
    setIsTyping(true);
    
    // Simulate AI thinking
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 800));
    
    const response = getAIResponse(userMsg);
    setIsTyping(false);
    setMessages(prev => [...prev, { from: 'bot', text: response }]);
  };

  const handleQuickReply = (reply) => {
    setMessage(reply);
    setTimeout(() => {
      const input = document.querySelector('[data-testid="chat-input"]');
      if (input) {
        input.focus();
      }
    }, 50);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 left-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-all duration-300 ${
          isOpen 
            ? 'bg-gray-800 text-white' 
            : 'bg-white text-black hover:bg-gray-100'
        }`}
        data-testid="chat-widget-btn"
      >
        {isOpen ? (
          <X className="w-5 h-5" />
        ) : (
          <>
            <div className="w-6 h-6 rounded-full bg-teal-400 flex items-center justify-center">
              <MessageCircle className="w-4 h-4 text-black" />
            </div>
            <span className="font-medium text-sm">Ask Kevin</span>
          </>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 left-6 w-80 md:w-96 bg-[#1a1a1f] rounded-2xl shadow-2xl border border-gray-800 z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="text-white font-medium">Kevin</h4>
                <p className="text-white/70 text-xs">AI Assistant • Online</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="h-72 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    msg.from === 'user'
                      ? 'bg-teal-400 text-black rounded-br-md'
                      : 'bg-[#0d1117] text-gray-300 rounded-bl-md'
                  }`}
                >
                  {msg.from === 'bot' && (
                    <div className="flex items-center gap-1.5 mb-1">
                      <Bot className="w-3 h-3 text-teal-400" />
                      <span className="text-xs text-teal-400">Kevin</span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-line">{msg.text}</p>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-[#0d1117] rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-teal-400 animate-spin" />
                    <span className="text-gray-400 text-sm">Kevin is typing...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {messages.length === 1 && (
            <div className="px-4 pb-3">
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((reply, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickReply(reply)}
                    className="bg-[#0d1117] hover:bg-[#252530] text-gray-300 text-xs px-3 py-1.5 rounded-full border border-gray-700 transition-colors"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-gray-800">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Type a message..."
                className="flex-1 bg-[#0d1117] border border-gray-800 rounded-lg px-4 py-2 text-white text-sm outline-none focus:border-teal-400 transition-colors"
                data-testid="chat-input"
              />
              <button
                onClick={handleSend}
                disabled={!message.trim() || isTyping}
                className="w-10 h-10 bg-teal-400 rounded-lg flex items-center justify-center hover:bg-teal-500 transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4 text-black" />
              </button>
            </div>
            <p className="text-gray-600 text-xs text-center mt-2">Powered by AIConneqtors</p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
